import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import TemplateFormManager from './dialog/TemplateFormManager';
import { SmsTemplate } from '@/hooks/sms/types';
import { useAuth } from '@/context/auth';
import { CheckCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';

interface RestaurantDetails {
  id: string;
  name: string;
  ordering_page_url?: string;
  review_page_url?: string;
  google_review_url?: string;
  facebook_url?: string;
  instagram_url?: string;
}

export interface CreateTemplateDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  restaurantId?: string;
  organisationId?: string; // Added organizationId prop
  onSuccess: () => void;
  onCreateTemplate?: (template: { name: string; content: string; category: string }) => void;
  templateToEdit?: SmsTemplate;
  initialContent?: string; // New prop to receive content from campaign composition
  restaurants?: RestaurantDetails[];
  isDuplicating?: boolean; // New prop to distinguish duplication from editing
}

const CreateTemplateDialog: React.FC<CreateTemplateDialogProps> = ({
  isOpen,
  onOpenChange,
  restaurantId,
  organisationId,
  onSuccess,
  onCreateTemplate,
  templateToEdit,
  initialContent,
  restaurants,
  isDuplicating = false
}) => {
  const { user } = useAuth();
  const isEditing = !!templateToEdit && !isDuplicating;
  const userRestaurants = restaurants || user?.restaurants || [];
  const [template, setTemplate] = useState({
    name: templateToEdit?.name || '',
    content: initialContent || templateToEdit?.content || '',
    category: templateToEdit?.category || 'Promotional' // Default to Promotional
  });
  
  // Update local state when templateToEdit or initialContent changes
  useEffect(() => {
    console.log("CreateTemplateDialog: Updating template with initialContent:", initialContent);
    setTemplate(prevTemplate => {
      const updatedTemplate = {
        name: templateToEdit?.name || prevTemplate.name,
        content: initialContent || templateToEdit?.content || prevTemplate.content,
        category: templateToEdit?.category || prevTemplate.category
      };
      console.log("CreateTemplateDialog: Updated template:", updatedTemplate);
      return updatedTemplate;
    });
  }, [templateToEdit, initialContent]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const handleSubmit = async () => {
    if (!restaurantId) {
      setError('No restaurant selected');
      return;
    }

    if (!template.name.trim()) {
      setError('Template name is required');
      return;
    }

    if (!template.content.trim()) {
      setError('Template content is required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // If onCreateTemplate callback exists, use it instead of making our own API call
      // This prevents duplicate template creation when used from MessageComposition
      if (onCreateTemplate) {
        onCreateTemplate(template);
      } else {
        // Only make direct API call if no callback is provided
        // Import the API service dynamically
        const { createTemplate, updateTemplate } = await import('@/services/smsApi');

        // Calculate estimated SMS units based on message content
        const hasUnicode = Array.from(template.content).some(char => char.charCodeAt(0) > 127);
        const charLimit = hasUnicode ? 70 : 160;
        const segments = Math.ceil(template.content.length / charLimit) || 1;

        if (isEditing && templateToEdit) {
          // Update existing template
          const updateData = {
            template_name: template.name,
            template_content: template.content,
            category: template.category || 'General',
            estimated_units: segments,
            restaurant_id: restaurantId // Include restaurant_id for query invalidation
          };

          console.log('Updating template with API call:', updateData);
          const result = await updateTemplate(templateToEdit.id, updateData);
          console.log('Template update API result:', result);
        } else {
          // Create new template
          const templateData = {
            restaurant_id: restaurantId,
            organisation_id: organisationId, // Include organisation_id when available
            template_name: template.name,
            template_content: template.content,
            category: template.category || 'General',
            estimated_units: segments // Include estimated units
          };

          console.log('Creating template with API call:', templateData);
          const result = await createTemplate(templateData);
          console.log('Template creation API result:', result);
        }
      }

      // Notify that operation was successful
      onSuccess();
      onOpenChange(false);
    } catch (err) {
      console.error('Error creating template:', err);
      setError(err instanceof Error ? err.message : 'Failed to save template');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleTemplateChange = (updatedTemplate: { name: string; content: string; category: string }) => {
    setTemplate(updatedTemplate);
  };
  
  const validateTemplate = () => {
    if (!template.name.trim() || !template.content.trim()) {
      return false;
    }
    return true;
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-w-[95vw] max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>
            {isEditing ? 'Edit Template' : isDuplicating ? 'Duplicate Template' : 'Create New Template'}
          </DialogTitle>
          <DialogDescription>
            {isDuplicating 
              ? 'Create a copy of this template. You can modify the name and content before saving.'
              : 'Create a reusable template for your SMS campaigns. You can include personalization variables that will be replaced with real customer data.'
            }
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto px-1">
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          <TemplateFormManager
            isSubmitting={isSubmitting}
            onTemplateChange={handleTemplateChange}
            validateTemplate={validateTemplate}
            error={error}
            restaurantId={restaurantId}
            initialContent={initialContent || templateToEdit?.content}
            initialName={templateToEdit?.name}
            initialCategory={templateToEdit?.category}
            restaurants={userRestaurants}
          />
        </div>
        
        <DialogFooter className="flex-shrink-0 mt-4">
          <div className="flex justify-between w-full">
            <Button 
              variant="ghost" 
              onClick={() => setTemplate({
                name: templateToEdit?.name || '',
                content: templateToEdit?.content || '',
                category: templateToEdit?.category || 'General'
              })} 
              disabled={isSubmitting}
            >
              Reset
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSubmit} 
                disabled={isSubmitting || !validateTemplate()}
                className="gap-2"
              >
                <CheckCircle className="h-4 w-4" />
                {isEditing ? 'Update Template' : isDuplicating ? 'Create Copy' : 'Save Template'}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateTemplateDialog;
